import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import InlineFeedback from '../InlineFeedback';
import { useFeedback } from '../../hooks/useFeedback';
import InstanceFeaturesTab from './InstanceFeaturesTab';
import JobFeaturesTab from './JobFeaturesTab';
import FeaturesTab from './FeaturesTab';
import TierFeaturesTab from './TierFeaturesTab';
import { SortOrder, usePageSize } from '../table/TableKit';
import {
  FeatureUpdate,
  InstanceFeatureUpdate,
  JobFeatureUpdate,
} from '../../types';
import { urlEnum, urlPositiveInt, urlString, useUrlState } from '../../hooks/useUrlState';
import { useSearchDraft } from '../../hooks/useSearchDraft';
import { usePageClamp } from '../../hooks/usePageClamp';

const URL_SPEC = {
  tab: urlEnum(['instance-features', 'job-features', 'features', 'tier-features'] as const, 'instance-features'),
  'instances.q': urlString(),
  'instances.ref': urlString(),
  'instances.sort': urlEnum(['analyzer_web', 'analyzer_cli', 'analyzer_api', 'converter_web', 'converter_cli', 'converter_api', 'instance_key', 'referrals', 'contact_email', 'created_at'] as const, 'instance_key'),
  'instances.order': urlEnum(['asc', 'desc'] as const, 'asc'),
  'instances.page': urlPositiveInt(),
  'jobs.q': urlString(),
  'jobs.sort': urlEnum(['analyzer_web', 'instance_key', 'operation', 'status', 'license_tokens', 'created_at'] as const, 'created_at'),
  'jobs.order': urlEnum(['asc', 'desc'] as const, 'desc'),
  'jobs.page': urlPositiveInt(),
};

// Drop the saved keys only. A toggle made during the save stays pending.
function withoutKeys(changes: Map<string, boolean>, saved: Map<string, boolean>): Map<string, boolean> {
  const next = new Map(changes);
  saved.forEach((_, key) => next.delete(key));
  return next;
}

// Feature toggle management: instance/job/global feature tabs with a shared
// dirty-state save bar. Moved out of AdminDashboard when it became a shell.
const FeaturesSection: React.FC = () => {
  const queryClient = useQueryClient();
  const [urlState, setUrlState] = useUrlState(URL_SPEC);
  const [pageSize, setPageSize] = usePageSize('admin-features');
  const [isSaving, setIsSaving] = useState(false);
  const saveFeedback = useFeedback();

  const [instanceSearchQuery, setInstanceSearchQuery] = useSearchDraft(
    urlState['instances.q'],
    (q) => setUrlState({ 'instances.q': q, 'instances.page': 1 }),
  );
  const [jobSearchQuery, setJobSearchQuery] = useSearchDraft(
    urlState['jobs.q'],
    (q) => setUrlState({ 'jobs.q': q, 'jobs.page': 1 }),
  );
  const selectedRef = urlState['instances.ref'] || null;

  const instanceOffset = (urlState['instances.page'] - 1) * pageSize;
  const jobOffset = (urlState['jobs.page'] - 1) * pageSize;
  const onInstanceSort = (sort: string, order: SortOrder) => setUrlState({
    'instances.sort': sort as typeof urlState['instances.sort'],
    'instances.order': order,
    'instances.page': 1,
  });
  const onJobSort = (sort: string, order: SortOrder) => setUrlState({
    'jobs.sort': sort as typeof urlState['jobs.sort'],
    'jobs.order': order,
    'jobs.page': 1,
  });

  const changePageSize = (size: number) => {
    setPageSize(size);
    setUrlState({ 'instances.page': 1, 'jobs.page': 1 });
  };

  const handleSelectRef = (ref: string | null) => {
    setUrlState({ 'instances.ref': ref ?? '', 'instances.page': 1 });
  };

  // Track changes for batch updates
  const [featureChanges, setFeatureChanges] = useState<Map<string, boolean>>(new Map());
  const [instanceFeatureChanges, setInstanceFeatureChanges] = useState<Map<string, boolean>>(new Map());
  const [jobFeatureChanges, setJobFeatureChanges] = useState<Map<string, boolean>>(new Map());
  // Derived, so a toggle made during a save keeps the bar visible.
  const isDirty = featureChanges.size > 0 || instanceFeatureChanges.size > 0 || jobFeatureChanges.size > 0;

  // Fetch data
  const { data: features = [], isLoading: featuresLoading, error: featuresError } = useQuery({
    queryKey: ['admin', 'features'],
    queryFn: apiService.getFeatures,
    retry: false,
  });

  const { data: instanceFeaturesData, isLoading: instanceFeaturesLoading, isPlaceholderData: instanceFeaturesPlaceholder, error: instanceFeaturesError } = useQuery({
    queryKey: ['admin', 'instanceFeatures', instanceOffset, pageSize, urlState['instances.q'], urlState['instances.sort'], urlState['instances.order'], selectedRef],
    queryFn: () => apiService.getInstanceFeatures(pageSize, instanceOffset, urlState['instances.q'] || undefined, urlState['instances.sort'], urlState['instances.order'], selectedRef || undefined),
    placeholderData: (previousData) => previousData,
    retry: false,
  });
  const instanceFeatures = React.useMemo(
    () => instanceFeaturesData?.items ?? [],
    [instanceFeaturesData]
  );

  const { data: jobFeaturesData, isLoading: jobFeaturesLoading, isPlaceholderData: jobFeaturesPlaceholder, error: jobFeaturesError } = useQuery({
    queryKey: ['admin', 'jobFeatures', jobOffset, pageSize, urlState['jobs.q'], urlState['jobs.sort'], urlState['jobs.order']],
    queryFn: () => apiService.getJobFeatures(pageSize, jobOffset, urlState['jobs.q'] || undefined, urlState['jobs.sort'], urlState['jobs.order']),
    placeholderData: (previousData) => previousData,
    retry: false,
  });
  const jobFeatures = React.useMemo(
    () => jobFeaturesData?.items ?? [],
    [jobFeaturesData]
  );

  usePageClamp({
    page: urlState['instances.page'],
    pageSize,
    total: instanceFeaturesData?.total,
    ready: !instanceFeaturesLoading && !instanceFeaturesPlaceholder && !!instanceFeaturesData,
    setPage: (page) => setUrlState({ 'instances.page': page }),
  });
  usePageClamp({
    page: urlState['jobs.page'],
    pageSize,
    total: jobFeaturesData?.total,
    ready: !jobFeaturesLoading && !jobFeaturesPlaceholder && !!jobFeaturesData,
    setPage: (page) => setUrlState({ 'jobs.page': page }),
  });

  // Handle changes
  const handleFeatureChange = (featureKey: string, isActive: boolean) => {
    const newChanges = new Map(featureChanges);
    const originalValue = features.find(f => f.feature_key === featureKey)?.is_active;

    if (originalValue === isActive) {
      newChanges.delete(featureKey);
    } else {
      newChanges.set(featureKey, isActive);
    }

    setFeatureChanges(newChanges);
  };

  const handleInstanceFeatureChange = (instanceId: number, featureKey: string, isEnabled: boolean) => {
    const key = `${instanceId}#${featureKey}`;
    const newChanges = new Map(instanceFeatureChanges);
    const instance = instanceFeatures.find(f => f.instance_id === instanceId);

    // Get the original value from the instance object
    const originalValue = instance ? (instance as any)[featureKey] : false;

    if (originalValue === isEnabled) {
      newChanges.delete(key);
    } else {
      newChanges.set(key, isEnabled);
    }

    setInstanceFeatureChanges(newChanges);
  };

  const handleJobFeatureChange = (jobId: string, featureKey: string, isEnabled: boolean) => {
    const key = `${jobId}#${featureKey}`;
    const newChanges = new Map(jobFeatureChanges);
    const job = jobFeatures.find(f => f.job_id === jobId);

    // Get the original value from the job object
    const originalValue = job ? (job as any)[featureKey] : false;

    if (originalValue === isEnabled) {
      newChanges.delete(key);
    } else {
      newChanges.set(key, isEnabled);
    }

    setJobFeatureChanges(newChanges);
  };

  // Save all changes
  const handleSave = async () => {
    setIsSaving(true);

    try {
      const saveOperations: Array<{
        label: string;
        queryKeys: string[][];
        promise: Promise<void>;
        clear: () => void;
      }> = [];

      if (featureChanges.size > 0) {
        const updates: FeatureUpdate[] = Array.from(featureChanges.entries()).map(([key, value]) => ({
          feature_key: key,
          is_active: value,
        }));
        saveOperations.push({
          label: 'features',
          // A global flag changes the effective value in the other two tabs.
          queryKeys: [['admin', 'features'], ['admin', 'instanceFeatures'], ['admin', 'jobFeatures']],
          promise: apiService.updateFeatures(updates),
          clear: () => setFeatureChanges((prev) => withoutKeys(prev, featureChanges)),
        });
      }

      if (instanceFeatureChanges.size > 0) {
        const updates: InstanceFeatureUpdate[] = Array.from(instanceFeatureChanges.entries()).map(([key, value]) => {
          const [instanceId, featureKey] = key.split('#');
          return {
            instance_id: parseInt(instanceId),
            feature_key: featureKey,
            is_enabled: value,
          };
        });
        saveOperations.push({
          label: 'instance features',
          queryKeys: [['admin', 'instanceFeatures']],
          promise: apiService.updateInstanceFeatures(updates),
          clear: () => setInstanceFeatureChanges((prev) => withoutKeys(prev, instanceFeatureChanges)),
        });
      }

      if (jobFeatureChanges.size > 0) {
        const updates: JobFeatureUpdate[] = Array.from(jobFeatureChanges.entries()).map(([key, value]) => {
          const [jobId, featureKey] = key.split('#');
          return {
            job_id: jobId,
            feature_key: featureKey,
            is_enabled: value,
          };
        });
        saveOperations.push({
          label: 'job features',
          queryKeys: [['admin', 'jobFeatures']],
          promise: apiService.updateJobFeatures(updates),
          clear: () => setJobFeatureChanges((prev) => withoutKeys(prev, jobFeatureChanges)),
        });
      }

      const results = await Promise.allSettled(saveOperations.map((update) => update.promise));
      const failedSections: string[] = [];
      const refreshQueries: Promise<void>[] = [];

      results.forEach((result, index) => {
        const update = saveOperations[index];
        if (result.status === 'fulfilled') {
          update.clear();
          update.queryKeys.forEach((queryKey) => {
            refreshQueries.push(queryClient.invalidateQueries({ queryKey }));
          });
        } else {
          failedSections.push(update.label);
          console.error(`Failed to save ${update.label}:`, result.reason);
        }
      });

      await Promise.all(refreshQueries);

      if (failedSections.length > 0) {
        saveFeedback.showError(`Failed to save ${failedSections.join(' and ')}. Try again.`);
      } else {
        saveFeedback.clear();
      }
    } catch (error) {
      console.error('Failed to save changes:', error);
      saveFeedback.showError('Failed to save changes. Try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel all changes
  const handleCancel = () => {
    setFeatureChanges(new Map());
    setInstanceFeatureChanges(new Map());
    setJobFeatureChanges(new Map());
  };

  const activeQuery = {
    'tier-features': { isLoading: featuresLoading, error: featuresError, message: 'Failed to load features.' },
    'instance-features': {
      isLoading: instanceFeaturesLoading,
      error: instanceFeaturesError,
      message: 'Failed to load instance features.',
    },
    'job-features': {
      isLoading: jobFeaturesLoading,
      error: jobFeaturesError,
      message: 'Failed to load job features.',
    },
    features: {
      isLoading: featuresLoading,
      error: featuresError,
      message: 'Failed to load features.',
    },
  }[urlState.tab];

  return (
    <div>
      <h2 className="text-xl font-bold text-heading mb-4">Features</h2>

      <InlineFeedback feedback={saveFeedback.feedback} className="mb-4 max-w-md" />

      {/* Action buttons */}
      {isDirty && (
        <div className="mb-6 flex justify-start gap-4">
          <button
            onClick={handleCancel}
            className="px-4 py-2 border border-secondary text-body-color rounded hover:bg-widget-bg"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="btn-primary"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 mb-6 border-b border-secondary">
        <button
          onClick={() => setUrlState({ tab: 'instance-features' })}
          className={`px-4 py-2 font-medium transition-colors ${
            urlState.tab === 'instance-features'
              ? 'text-primary border-b-2 border-primary'
              : 'text-secondary hover:text-body-color'
          }`}
        >
          Instance Features
        </button>
        <button
          onClick={() => setUrlState({ tab: 'job-features' })}
          className={`px-4 py-2 font-medium transition-colors ${
            urlState.tab === 'job-features'
              ? 'text-primary border-b-2 border-primary'
              : 'text-secondary hover:text-body-color'
          }`}
        >
          Job Features
        </button>
        <button
          onClick={() => setUrlState({ tab: 'features' })}
          className={`px-4 py-2 font-medium transition-colors ${
            urlState.tab === 'features'
              ? 'text-primary border-b-2 border-primary'
              : 'text-secondary hover:text-body-color'
          }`}
        >
          Global Features
        </button>
        <button
          onClick={() => setUrlState({ tab: 'tier-features' })}
          className={`px-4 py-2 font-medium transition-colors ${urlState.tab === 'tier-features' ? 'text-primary border-b-2 border-primary' : 'text-secondary hover:text-body-color'}`}
        >Tier Features</button>
      </div>

      {!featuresLoading && !featuresError && <div hidden={urlState.tab !== 'tier-features'}><TierFeaturesTab features={features} active={urlState.tab === 'tier-features'} /></div>}

      {/* Tab content */}
      {activeQuery.error ? (
        <InlineFeedback feedback={{ kind: 'error', message: activeQuery.message }} className="max-w-md" />
      ) : activeQuery.isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          {urlState.tab === 'instance-features' && (
            <InstanceFeaturesTab
              instanceFeatures={instanceFeatures}
              changes={instanceFeatureChanges}
              onFeatureChange={handleInstanceFeatureChange}
              searchQuery={instanceSearchQuery}
              onSearchChange={setInstanceSearchQuery}
              selectedRef={selectedRef}
              onSelectRef={handleSelectRef}
              total={instanceFeaturesData?.total ?? 0}
              offset={instanceOffset}
              pageSize={pageSize}
              onOffsetChange={(offset) => setUrlState({ 'instances.page': offset / pageSize + 1 })}
              onPageSizeChange={changePageSize}
              sort={urlState['instances.sort']}
              order={urlState['instances.order']}
              onSort={onInstanceSort}
            />
          )}
          {urlState.tab === 'job-features' && (
            <JobFeaturesTab
              jobFeatures={jobFeatures}
              changes={jobFeatureChanges}
              onFeatureChange={handleJobFeatureChange}
              searchQuery={jobSearchQuery}
              onSearchChange={setJobSearchQuery}
              total={jobFeaturesData?.total ?? 0}
              offset={jobOffset}
              pageSize={pageSize}
              onOffsetChange={(offset) => setUrlState({ 'jobs.page': offset / pageSize + 1 })}
              onPageSizeChange={changePageSize}
              sort={urlState['jobs.sort']}
              order={urlState['jobs.order']}
              onSort={onJobSort}
            />
          )}
          {urlState.tab === 'features' && (
            <FeaturesTab
              features={features}
              changes={featureChanges}
              onFeatureChange={handleFeatureChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default FeaturesSection;
